import { Box, SxProps, Theme } from "@mui/material";
import type { JSX } from "react";
import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FormControlSelect } from "@/components/formControls/formControlSelect/FormControlSelect";
import { FormControlToggleButtonGroup, ToggleOption } from "@/components/formControls/formControlToggleButtonGroup";
import {
  getHouseBuildingTypeOptions,
  getObjectTypeOptions,
  getOwnershipOptions,
  optionsByObjectType,
} from "@/helpers/realEstateParametersHelpers";
import { useGetErrorMessage } from "@/hooks/useGetErrorMessage";
import { useModuleContext } from "@/hooks/useModuleContext";
import { ActualUseEnum, BuildingTypeEnum, ObjectTypeEnum, OwnershipEnum } from "@/types/refineRealEstate";
import { PropertyInformation } from "@/types/refineRealEstate/refineRealEstate";

import {
  StyledFormGridContainer,
  StyledFormSection,
  StyledFormSectionHeader,
  StyledFormSectionTitle,
} from "../refineRealEstate.styled";

const toggleButtonGroupStyle: SxProps<Theme> = {
  height: "45px",
  gap: "4px",
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
};

interface GeneralInfoTabProps {
  readonly?: boolean;
  isObjectTypeReadOnly?: boolean;
}

export const GeneralInfoTab = ({ readonly, isObjectTypeReadOnly }: GeneralInfoTabProps): JSX.Element => {
  const { t } = useTranslation("Common", { keyPrefix: "realEstate" });
  const getErrorMessage = useGetErrorMessage();

  const {
    control,
    watch,
    formState: { errors },
    setValue,
    trigger,
  } = useFormContext<PropertyInformation>();

  const { isOneClick, isValuationPackage, isValuationReport } = useModuleContext();
  const { generalInfo, energyAndPhotovoltaic } = watch();

  const isSingleFamilyHouse = generalInfo.buildingType === BuildingTypeEnum.singleFamilyHouse;
  const isApartment = generalInfo.buildingType === BuildingTypeEnum.apartment;
  const isMultiFamilyHouse = generalInfo.buildingType === BuildingTypeEnum.multiFamilyHouse;
  const isMixedUseProperty = generalInfo.buildingType === BuildingTypeEnum.mixedUseProperty;
  const isPlot = generalInfo.objectType === ObjectTypeEnum.plotOfLand;

  const isLeasehold = generalInfo.ownership
    ? ([OwnershipEnum.leasehold, OwnershipEnum.leaseholdApartmentOwnership] as OwnershipEnum[]).includes(
        generalInfo.ownership,
      )
    : false;
  const isSelfUseSuitabilityActive =
    //generalInfo.actualUse === ActualUseEnum.empty ||
    generalInfo.actualUse === ActualUseEnum.rented;

  const ownershipOptions = getOwnershipOptions(t)
    .filter((option) => optionsByObjectType[generalInfo.objectType].includes(option.value))
    .map((option) =>
      option.value !== OwnershipEnum.fullOwnership && isValuationPackage ? { ...option, disabled: true } : option,
    );

  const objectTypeOptions = getObjectTypeOptions(t).map((option) =>
    option.value !== ObjectTypeEnum.house && option.value !== ObjectTypeEnum.apartment && isValuationPackage
      ? { ...option, disabled: true }
      : option,
  );
  const houseBuildingTypeOptions = getHouseBuildingTypeOptions(t);
  const validHouseBuildingTypes = houseBuildingTypeOptions.map((option) => option.value);

  const usageOptions: ToggleOption<ActualUseEnum>[] = useMemo(
    () => [
      { value: ActualUseEnum.ownerOccupied, label: t("actualUsage.values.ownerOccupied"), disabled: !!readonly },
      { value: ActualUseEnum.rented, label: t("actualUsage.values.rented"), disabled: !!readonly },
      //{ value: ActualUseEnum.empty, label: t("actualUsage.values.empty") },
      { value: ActualUseEnum.partialOwnerOccupied, label: t("actualUsage.values.partialOwnerOccupied") },
    ],
    [readonly, t],
  );
  const isSpecialCategory = generalInfo.buildingType
    ? (
        [BuildingTypeEnum.twoFamilyHouse, BuildingTypeEnum.singleFamilyHouseWithGrannyApartment] as BuildingTypeEnum[]
      ).includes(generalInfo.buildingType)
    : false;

  const filteredUsageOptions = usageOptions.filter((option) => {
    return isSpecialCategory || option.value !== ActualUseEnum.partialOwnerOccupied;
  });

  const ownUsageOptions: ToggleOption<boolean>[] = [
    { value: true, label: t("yes"), disabled: !isSelfUseSuitabilityActive },
    { value: false, label: t("no"), disabled: !isSelfUseSuitabilityActive },
  ];

  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    if (generalInfo.ownership && !optionsByObjectType[generalInfo.objectType].includes(generalInfo.ownership)) {
      setValue("generalInfo.ownership", ownershipOptions[0].value);
    }
  }, [generalInfo.objectType, generalInfo.ownership, ownershipOptions, setValue]);

  useEffect(() => {
    switch (generalInfo.objectType) {
      case ObjectTypeEnum.house:
        if (!generalInfo.buildingType || !validHouseBuildingTypes.includes(generalInfo.buildingType)) {
          setValue("generalInfo.buildingType", BuildingTypeEnum.singleFamilyHouse);
        }
        break;
      case ObjectTypeEnum.apartment:
        if (generalInfo.buildingType !== BuildingTypeEnum.apartment) {
          setValue("generalInfo.buildingType", BuildingTypeEnum.apartment);
        }
        break;
      case ObjectTypeEnum.multiFamilyHouse:
        if (generalInfo.buildingType !== BuildingTypeEnum.multiFamilyHouse) {
          setValue("generalInfo.buildingType", BuildingTypeEnum.multiFamilyHouse);
        }
        break;
      case ObjectTypeEnum.mixedUseProperty:
        if (generalInfo.buildingType !== BuildingTypeEnum.mixedUseProperty) {
          setValue("generalInfo.buildingType", BuildingTypeEnum.mixedUseProperty);
        }
        break;
      case ObjectTypeEnum.plotOfLand:
        if (generalInfo.buildingType !== BuildingTypeEnum.plotArea) {
          setValue("generalInfo.buildingType", BuildingTypeEnum.plotArea);
        }
        break;
    }
  }, [generalInfo.objectType, generalInfo.buildingType, setValue, validHouseBuildingTypes]);

  useEffect(() => {
    if (!isLeasehold) {
      setValue("generalInfo.remainingDuration", undefined);
      setValue("generalInfo.actualGroundRent", undefined);
      setValue("generalInfo.nonCompensableBuildingPortion", undefined);
    }
  }, [isLeasehold, setValue]);

  useEffect(() => {
    if (!isSelfUseSuitabilityActive) {
      setValue("generalInfo.selfUseSuitability", true);
    }
  }, [generalInfo.actualUse, isSelfUseSuitabilityActive, setValue]);

  useEffect(() => {
    if (
      (isApartment || isMultiFamilyHouse || isMixedUseProperty) &&
      energyAndPhotovoltaic.photovoltaicAvailable !== undefined
    ) {
      setValue("energyAndPhotovoltaic.photovoltaicAvailable", undefined);
    }
  }, [
    setValue,
    energyAndPhotovoltaic.photovoltaicAvailable,
    isSingleFamilyHouse,
    isApartment,
    isMultiFamilyHouse,
    isMixedUseProperty,
  ]);

  useEffect(() => {
    if (
      (isApartment || isMultiFamilyHouse || isMixedUseProperty) &&
      energyAndPhotovoltaic.constructionYear !== undefined
    ) {
      setValue("energyAndPhotovoltaic.constructionYear", undefined);
    }
  }, [
    setValue,
    energyAndPhotovoltaic.constructionYear,
    isSingleFamilyHouse,
    isApartment,
    isMultiFamilyHouse,
    isMixedUseProperty,
  ]);

  useEffect(() => {
    if (
      (isApartment || isMultiFamilyHouse || isMixedUseProperty) &&
      energyAndPhotovoltaic.annualPowerYield !== undefined
    ) {
      setValue("energyAndPhotovoltaic.annualPowerYield", undefined);
    }
  }, [
    setValue,
    energyAndPhotovoltaic.annualPowerYield,
    isSingleFamilyHouse,
    isApartment,
    isMultiFamilyHouse,
    isMixedUseProperty,
  ]);

  return (
    <>
      <StyledFormGridContainer>
        <StyledFormSection>
          <StyledFormSectionTitle>{t("objectCategory.title")}</StyledFormSectionTitle>
          <FormControlSelect
            name="generalInfo.objectType"
            control={control}
            value={generalInfo.objectType}
            options={objectTypeOptions}
            fullWidth
            selectSx={{ height: "45px" }}
            disabled={true}
          />
        </StyledFormSection>

        <StyledFormSection>
          <StyledFormSectionTitle>{t("ownership.title")}</StyledFormSectionTitle>
          <FormControlSelect
            name="generalInfo.ownership"
            control={control}
            options={ownershipOptions}
            fullWidth
            selectSx={{ height: "45px" }}
            disabled={!isOneClick || readonly}
            errorMsg={getErrorMessage(errors.generalInfo?.ownership)}
            data-cy="ownership-select"
          />
        </StyledFormSection>

        {generalInfo.objectType === ObjectTypeEnum.house && (
          <FormControlSelect
            name="generalInfo.buildingType"
            control={control}
            value={generalInfo.buildingType}
            options={houseBuildingTypeOptions}
            fullWidth
            selectSx={{ height: "45px" }}
            disabled={isValuationReport || readonly || isObjectTypeReadOnly ? true : false}
          />
        )}

        {!isPlot && !isMultiFamilyHouse && !isMixedUseProperty && (
          <>
            <StyledFormSectionHeader>{t("usage")}</StyledFormSectionHeader>
            <Box sx={{ gridColumn: "span 2" }}>
              <FormControlToggleButtonGroup
                label={t("actualUsage.title")}
                control={control}
                name="generalInfo.actualUse"
                fullWidth
                sx={toggleButtonGroupStyle}
                options={filteredUsageOptions}
                data-cy="actual-use-select"
              />
            </Box>

            {isSelfUseSuitabilityActive && (
              <Box sx={{ gridColumn: "span 2" }}>
                <FormControlToggleButtonGroup
                  label={t("actualUsage.selfUseSuitability")}
                  control={control}
                  name="generalInfo.selfUseSuitability"
                  fullWidth
                  sx={toggleButtonGroupStyle}
                  options={ownUsageOptions}
                  data-cy="self-use-suitability-select"
                />
              </Box>
            )}
          </>
        )}

        {/* {isLeasehold && (
          <>
            <StyledFormSectionHeader>{t("leaseholdRight.title")}</StyledFormSectionHeader>

            <StyledFormSection>
              <StyledFormSectionTitle>{t("leaseholdRight.remainingDuration")}</StyledFormSectionTitle>

              <FormControlNumberInput
                name="generalInfo.remainingDuration"
                control={control}
                fullWidth
                error={!!errors.generalInfo?.remainingDuration}
                helperText={getErrorMessage(errors.generalInfo?.remainingDuration)}
                disabled={!!readonly}
              />
            </StyledFormSection>

            <StyledFormSection>
              <StyledFormSectionTitle>{t("leaseholdRight.actualGroundRent")}</StyledFormSectionTitle>
              <FormControlNumberInput
                name="generalInfo.actualGroundRent"
                control={control}
                fullWidth
                error={!!errors.generalInfo?.actualGroundRent}
                helperText={getErrorMessage(errors.generalInfo?.actualGroundRent)}
                disabled={!!readonly}
              />
            </StyledFormSection>

            <StyledFormSection>
              <StyledFormSectionTitle>{t("leaseholdRight.nonCompensableBuildingPortion")}</StyledFormSectionTitle>
              <FormControlNumberInput
                name="generalInfo.nonCompensableBuildingPortion"
                control={control}
                fullWidth
                error={!!errors.generalInfo?.nonCompensableBuildingPortion}
                helperText={getErrorMessage(errors.generalInfo?.nonCompensableBuildingPortion)}
                disabled={!!readonly}
              />
            </StyledFormSection>

            <Box />
          </>
        )} */}
      </StyledFormGridContainer>
    </>
  );
};
